import {
  CtaLink,
  Hero,
  ImageTextBlock,
  HeaderTextBlock,
  PageSection,
  PageSectionContent,
} from '@greystar/designsystem';
import {
  Property,
  GetLevelsResponse,
  getLocationGuideLink,
  withBooleanFeatureFlag,
  getLinkIcon,
} from '@greystar/lib';
import { PropertyDetailsNeighborhoodProps, ResponsiveImage } from '@greystar/ui';
import { GetStaticComponentProps, withDatasourceCheck } from '@sitecore-jss/sitecore-jss-nextjs';
import React, { JSX } from 'react';

const PropertyDetailsNeighborhood = (props: PropertyDetailsNeighborhoodProps): JSX.Element => {
  if (
    !props.data.neighbourhoodImage ||
    !props.data.neighbourhoodLink ||
    !props.data.neighbourhoodText
  ) {
    return <></>;
  }

  return (
    <PageSection>
      <PageSectionContent>
        <ImageTextBlock direction="reverse">
          <ImageTextBlock.Content>
            <Hero hasPadding={false}>
              <Hero.HeaderTextBlock>
                <HeaderTextBlock.Header header={props.fields?.title.value} />
              </Hero.HeaderTextBlock>
              <Hero.BodyCopy>{props.data.neighbourhoodText}</Hero.BodyCopy>
              <Hero.Ctas>
                <CtaLink
                  href={props.data.neighbourhoodLink}
                  label={props.fields?.ctaText?.value ?? ''}
                  variant="secondary"
                  icon={getLinkIcon()}
                />
              </Hero.Ctas>
            </Hero>
          </ImageTextBlock.Content>

          <ImageTextBlock.Image borderColour="sunshine" borderRounded={true} borderType="left">
            <ResponsiveImage
              image={props.data.neighbourhoodImage}
              fill={true}
              sizes="(max-width: 600px) 100vw, (max-width: 1024px) 50vw, 25vw"
            />
          </ImageTextBlock.Image>
        </ImageTextBlock>
      </PageSectionContent>
    </PageSection>
  );
};

export const getStaticProps: GetStaticComponentProps = async (
  rendering,
  layoutData
): Promise<PropertyDetailsNeighborhoodProps> => {
  const props = rendering as unknown as PropertyDetailsNeighborhoodProps;
  const property = layoutData.sitecore.context.property as Property;

  const response: PropertyDetailsNeighborhoodProps = {
    ...props,
    data: {
      notFound: true,
      neighbourhoodImage: null,
      neighbourhoodText: null,
      neighbourhoodLink: null,
    },
    flagKey: rendering.componentName,
    isPageEditing: layoutData.sitecore.context.pageEditing,
  };

  if (!property) {
    return response;
  }

  const locationsData = layoutData.sitecore.context.locationsData as GetLevelsResponse;

  if (locationsData?.search?.results?.length) {
    if (property?.location?.greystarNeighborhood && property.location.greystarNeighborhood !== '') {
      const neighborhoodData = locationsData.search.results.find(
        (result) =>
          result.neighborhoodName &&
          result.neighborhoodName.value.toLowerCase() ===
            property.location?.greystarNeighborhood?.toLowerCase()
      );

      if (neighborhoodData) {
        return {
          ...response,
          data: {
            ...response.data,
            notFound: false,
            neighbourhoodText:
              neighborhoodData.metaDescription && neighborhoodData.metaDescription.value !== ''
                ? neighborhoodData.metaDescription.value
                : null,
            neighbourhoodImage: neighborhoodData.tileImageAEM ?? null,
            neighbourhoodLink:
              neighborhoodData.url?.path && neighborhoodData.url.path != ''
                ? getLocationGuideLink(neighborhoodData.url.path)
                : null,
          },
        };
      }
    }

    if (property?.location?.greystarMarketArea && property.location.greystarMarketArea !== '') {
      const marketAreaData = locationsData.search.results.find(
        (result) =>
          result.marketAreaName &&
          result.marketAreaName.value.toLowerCase() ===
            property.location?.greystarMarketArea?.toLowerCase()
      );

      if (marketAreaData) {
        return {
          ...response,
          data: {
            ...response.data,
            notFound: false,
            neighbourhoodText:
              marketAreaData.metaDescription && marketAreaData.metaDescription.value !== ''
                ? marketAreaData.metaDescription.value
                : null,
            neighbourhoodImage: marketAreaData.tileImageAEM ?? null,
            neighbourhoodLink:
              marketAreaData.url?.path && marketAreaData.url.path != ''
                ? getLocationGuideLink(marketAreaData.url.path)
                : null,
          },
        };
      }
    }
  }

  return {
    ...response,
    data: {
      ...response.data,
      notFound: false,
    },
  };
};

export default withDatasourceCheck()<PropertyDetailsNeighborhoodProps>(
  withBooleanFeatureFlag(PropertyDetailsNeighborhood)
);
